import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import StatsBar from "@/components/site/StatsBar";
import Seo from "@/components/Seo";

import FlatIcon from "@/components/site/FlatIcon";
import {
  Check,
  UserPlus,
  LogIn,
  CreditCard,
  KeyRound,
  ShieldCheck,
  Zap,
  Network,
  Layers,
  Server,
  MessageSquare,
  Phone,
  Sparkles,
  LockKeyhole,
  UserX,
  Rocket,
  Scale,
} from "lucide-react";


const useCases = [
  { title: "New Account Verification", description: "Confirm real phone numbers at signup and block fake or bot accounts before they enter your system.", Icon: UserPlus, color: "text-amber" },
  { title: "Login Verification", description: "Add a second layer of protection to every login, without requiring users to install an authenticator app.", Icon: LogIn, color: "text-sky" },
  { title: "Transaction Confirmation", description: "Verify high-value or sensitive transactions with a one-time code before they're approved.", Icon: CreditCard, color: "text-violet" },
  { title: "Password Reset", description: "Replace insecure email resets with an OTP sent directly to a verified phone number.", Icon: KeyRound, color: "text-navy" },
];

const benefits = [
  { title: "Stronger Security", description: "Add a verification layer that's harder to intercept than email-based codes.", Icon: LockKeyhole, color: "text-amber" },
  { title: "Reduced Fraud & Fake Accounts", description: "Filter out bots and fraudulent sign-ups before they reach your system.", Icon: UserX, color: "text-sky" },
  { title: "Faster Onboarding", description: "Verify real users in seconds, without app downloads or extra steps.", Icon: Rocket, color: "text-lime" },
  { title: "Regulatory Alignment", description: "Support KYC and strong customer authentication requirements common in banking and fintech.", Icon: Scale, color: "text-violet" },
];

const industries = [
  "Banks & Financial Institutions",
  "Fintech & Payment Platforms",
  "Mobile Network Operators (MNOs)",
  "E-commerce & Marketplaces",
  "Enterprise SaaS Platforms",
];

const whyYootelco = [
  { title: "Direct Carrier Connections", description: "Reach mobile networks directly for faster, more reliable delivery.", Icon: Network, gradient: "icon-tile-teal" },
  { title: "Multi-Channel Delivery", description: "Send OTPs over SMS or RCS from a single API.", Icon: Layers, gradient: "icon-tile-indigo" },
  { title: "Enterprise-Grade Reliability", description: "Built for high-volume, mission-critical verification traffic.", Icon: Server, gradient: "icon-tile-blue" },
];

const channels = [
  { title: "SMS", description: "Deliver OTP codes over the most universally reachable channel.", Icon: MessageSquare, gradient: "icon-tile-teal" },
  { title: "RCS", description: "Use rich, interactive messaging for OTP delivery on supported devices.", Icon: Zap, gradient: "icon-tile-indigo" },
  { title: "Voice", description: "Deliver codes by automated voice call as a fallback channel.", Icon: Phone, gradient: "icon-tile-blue" },
];

const faqs = [
  { question: "Why is OTP authentication important?", answer: "OTP authentication confirms that the person accessing an account or approving a transaction is the real, verified user. It reduces fraud, blocks fake sign-ups, and adds a compliance-friendly layer of security for regulated platforms." },
  { question: "How fast are OTP codes delivered?", answer: "Because Yootelco connects directly to mobile networks, OTP codes are typically delivered within seconds. Delivery speed can vary by destination country and channel." },
  { question: "Can I send OTPs over more than one channel?", answer: "Yes. Yootelco supports OTP delivery over SMS and RCS, so you can choose the channel your users respond to best, or set up fallback routing between them." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const SolutionOtpAuthentication = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="min-h-screen bg-white">
      <Seo
        title="OTP Authentication API via SMS, RCS & Voice | Yootelco"
        description="Send secure, time-limited one-time passwords via SMS, RCS, and voice. Built for banks, fintechs, and telecoms to verify users fast."
        path="/solutions/otp-authentication"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[760px] text-center">
            <p className="text-[13px] font-bold uppercase tracking-[0.14em] text-primary">Verify Every User, Instantly</p>
            <h1 className="mt-[14px] font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              OTP Authentication
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Send secure, time-limited one-time passwords via SMS and RCS. Yootelco's OTP API helps banks, fintechs, and telecoms verify users in real time, without slowing down onboarding.
            </p>
            <div className="mt-[28px] flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What is OTP authentication */}
      <section className="section-shell pt-[20px]">
        <div className="mx-auto max-w-[900px]">
          <h2 className="text-[28px] font-bold leading-[1.2] tracking-tight text-foreground md:text-[32px]">What Is OTP Authentication?</h2>
          <p className="mt-[16px] text-[14.5px] leading-[1.65] text-muted-foreground">
            One-time password (OTP) authentication verifies a user's identity by sending a short, single-use code to their phone during login, signup, or a sensitive transaction. Because the code expires within minutes and can only be used once, OTPs add a strong layer of protection beyond a username and password.
          </p>
          <p className="mt-[14px] text-[14.5px] leading-[1.65] text-muted-foreground">
            For regulated industries like banking and fintech, OTP is often a compliance requirement, and for consumer platforms, it's one of the fastest ways to confirm a real, reachable user without adding friction to sign-up.
          </p>
        </div>
      </section>

      {/* Common use cases */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">Common Use Cases</h2>
        </div>
        <div className="mx-auto mt-[32px] flex max-w-[1120px] flex-wrap items-stretch justify-center gap-4">
          {useCases.map((c) => (
            <article
              key={c.title}
              className="w-full rounded-[16px] border border-border/70 bg-secondary/40 p-[24px] sm:w-[calc(50%-0.5rem)] lg:w-[calc(25%-0.75rem)]"
            >
              <FlatIcon Icon={c.Icon} color={c.color} />
              <h3 className="mt-[14px] text-[16px] font-bold text-foreground">{c.title}</h3>
              <p className="mt-[8px] text-[13px] leading-[1.55] text-muted-foreground">{c.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* OTP with Yootelco */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px]">
          <h2 className="text-[28px] font-bold leading-[1.2] tracking-tight text-foreground md:text-[32px]">OTP Authentication With Yootelco</h2>
          <p className="mt-[16px] text-[14.5px] leading-[1.65] text-muted-foreground">
            As a UK-based enterprise messaging provider with direct routes to mobile network operators, Yootelco delivers OTP codes with high deliverability and low latency across SMS and RCS. Our platform is built for regulated industries, banks, fintechs, and MNOs, that can't afford delayed or dropped verification codes.
          </p>
        </div>
      </section>

      {/* Key benefits */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">Key Benefits</h2>
        </div>
        <div className="mx-auto mt-[32px] flex max-w-[1080px] flex-wrap justify-center gap-5">
          {benefits.map((b, i) => (
            <article key={b.title} className="w-full rounded-[16px] border border-border/70 bg-secondary/40 p-[24px] text-center sm:w-[calc(50%-0.625rem)] lg:w-[calc(25%-0.94rem)]">
              <FlatIcon Icon={b.Icon} color={b.color} className="mx-auto block w-fit" />
              <h3 className="mt-[14px] text-[17px] font-bold text-foreground">{b.title}</h3>
              <p className="mx-auto mt-[8px] max-w-[260px] text-[13px] leading-[1.55] text-muted-foreground">{b.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Industries we serve */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px]">
          <h2 className="text-[28px] font-bold leading-[1.2] tracking-tight text-foreground md:text-[32px]">Industries We Serve</h2>
          <ul className="mt-[18px] grid gap-[14px] sm:grid-cols-2">
            {industries.map((item) => (
              <li key={item} className="flex items-start gap-3 text-[14.5px] leading-[1.55] text-foreground">
                <span className="mt-[2px] flex h-[20px] w-[20px] flex-none items-center justify-center rounded-full bg-primary/10">
                  <Check className="h-3 w-3 text-primary" strokeWidth={3} />
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Why choose Yootelco */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">Why Choose Yootelco</h2>
        </div>
        <div className="mx-auto mt-[32px] flex max-w-[1080px] flex-wrap justify-center gap-5">
          {whyYootelco.map((c) => (
            <article key={c.title} className="w-full rounded-[16px] border border-border/70 bg-secondary/40 p-[24px] sm:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-0.834rem)]">
              <span className={`flex h-11 w-11 items-center justify-center rounded-[12px] ${c.gradient}`}>
                <c.Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="mt-[14px] text-[17px] font-bold text-foreground">{c.title}</h3>
              <p className="mt-[8px] text-[13px] leading-[1.55] text-muted-foreground">{c.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Supported channels */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">Supported Channels</h2>
        </div>
        <div className="mx-auto mt-[32px] flex max-w-[1120px] flex-wrap items-stretch justify-center gap-4">
          {channels.map((c) => (
            <article
              key={c.title}
              className="flex h-full w-full items-start gap-3 rounded-[14px] border border-border/70 bg-secondary/40 px-[18px] py-[16px] sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)]"
            >
              <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-[10px] ${c.gradient}`}>
                <c.Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="text-[14px] font-bold text-foreground">{c.title}</h3>
                <p className="mt-[4px] text-[12.5px] leading-[1.5] text-muted-foreground">{c.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <StatsBar />

      {/* CTA banner */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[1120px] rounded-[24px] border border-border/70 bg-secondary/40 px-6 py-10 text-center md:px-10 md:py-12">
          <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-[12px] bg-gradient-to-br from-emerald-400 to-teal-500 shadow-sm">
            <ShieldCheck className="h-5 w-5" strokeWidth={2.4} />
          </span>
          <h2 className="mt-[14px] text-[24px] font-bold leading-[1.2] tracking-tight text-foreground md:text-[28px]">Built for Regulated Industries</h2>
          <p className="mx-auto mt-[12px] max-w-[720px] text-[14.5px] leading-[1.65] text-muted-foreground">
            Yootelco's messaging infrastructure is designed for banks, fintechs, and telecoms that need every OTP to arrive, securely and on time. Talk to our team about your verification volume and compliance requirements.
          </p>
          <Link
            to="/contact"
            className="mt-[22px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
          >
            Contact Sales
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
            <CTASection
        title={<><span className="text-sky">Ready</span> to Secure Your User Verification?</>}
        description="Talk to our team about adding OTP authentication to your platform."
        useCase="OTP Authentication"
      />

      <SiteFooter />
    </main>
  );
};

export default SolutionOtpAuthentication;
